import React, { useState, useEffect } from 'react';
import { PRODUCT_NAME } from '@cipherops/data-source';
import { invoke, IpcError } from '../lib/ipc';
import '../styles/UnlockScreen.css';

interface UnlockScreenProps {
  onUnlock: () => void;
  isFirstRun?: boolean;
}

export default function UnlockScreen({ onUnlock, isFirstRun = false }: UnlockScreenProps) {
  const [password, setPassword]           = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError]                 = useState('');
  const [lockedUntil, setLockedUntil]     = useState<Date | null>(null);
  const [isLoading, setIsLoading]         = useState(false);
  const [countdown, setCountdown]         = useState(0);

  useEffect(() => {
    if (!lockedUntil) return;
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((lockedUntil.getTime() - Date.now()) / 1000));
      setCountdown(remaining);
      if (remaining <= 0) setLockedUntil(null);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [lockedUntil]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (lockedUntil && lockedUntil > new Date()) return;

    if (!password.trim()) {
      setError('Password required');
      return;
    }

    if (isFirstRun && password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (isFirstRun && password.length < 12) {
      setError('Password must be at least 12 characters');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await invoke('cipherops/auth/unlock', { password });
      onUnlock();
    } catch (err) {
      if (err instanceof IpcError) {
        if (err.code === 'AUTH_LOCKED') {
          const details = err.details as { locked_out_until?: string } | undefined;
          if (details?.locked_out_until) setLockedUntil(new Date(details.locked_out_until));
          setError('Too many failed attempts. Please wait before trying again.');
        } else if (err.code === 'AUTH_WRONG_PASSWORD') {
          const details = err.details as { locked_out_until?: string | null } | undefined;
          if (details?.locked_out_until) setLockedUntil(new Date(details.locked_out_until));
          setError('Incorrect password.');
        } else if (err.code === 'NOT_IN_ELECTRON') {
          onUnlock();
        } else {
          setError(err.message);
        }
      } else {
        setError(err instanceof Error ? err.message : 'Unlock failed');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const isLocked = lockedUntil !== null && lockedUntil > new Date();

  return (
    <div className="unlock-screen">
      <div className="unlock-card">
        <div className="unlock-header">
          <h1>{PRODUCT_NAME}</h1>
          <p>{isFirstRun ? 'Set a master password to secure your workspace' : 'Penetration Testing Reports'}</p>
        </div>

        <form onSubmit={handleSubmit} className="unlock-form">
          <label htmlFor="password">
            {isFirstRun ? 'New Master Password' : 'Master Password'}
          </label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={isFirstRun ? 'At least 12 characters' : 'Enter master password'}
            disabled={isLoading || isLocked}
            autoFocus
          />

          {isFirstRun && (
            <>
              <label htmlFor="confirm">Confirm Password</label>
              <input
                id="confirm"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter password"
                disabled={isLoading}
              />
            </>
          )}

          {error && (
            <div className="error-message">
              {error}
              {isLocked && countdown > 0 && (
                <span style={{ marginLeft: 8, fontFamily: 'var(--mono)', fontSize: 12 }}>
                  ({countdown}s)
                </span>
              )}
            </div>
          )}

          <button type="submit" disabled={isLoading || isLocked}>
            {isLoading ? 'Unlocking...' : isFirstRun ? 'Create & Unlock' : 'Unlock'}
          </button>
        </form>

        <div className="unlock-footer">
          <p>v0.1.0 — {PRODUCT_NAME} Phase 1</p>
        </div>
      </div>
    </div>
  );
}
